<template>
  <div
    ref="containerRef"
    class="f-marquee-text"
    :class="animationClasses"
    :style="containerStyle"
  >
    <div class="f-marquee-text__content">
      <p ref="textRef">
        <slot />
      </p>

      <template v-if="showAnimation">
        <p
          v-for="count in 3"
          :key="count"
          aria-hidden="true"
        >
          <slot />
        </p>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useElementSize } from '@vueuse/core'

// Example: https://github.com/SchnapsterDog/vue3-marquee-slider/blob/master/src/components/VueMarqueeSlider.vue
const props = withDefaults(
  defineProps<{
    space?: number
    speed?: number
    paused?: boolean
    reverse?: boolean
    showAnimationOverride?: boolean
  }>(),
  {
    space: 50,
    speed: 40,
    showAnimationOverride: undefined,
  },
)

const containerRef = ref<HTMLElement | null>(null)
const textRef = ref<HTMLElement | null>(null)

const { width: containerWidth } = useElementSize(containerRef)
const { width: textWidth } = useElementSize(textRef)
const showAnimation = computed(() => {
  if (props.showAnimationOverride !== undefined) {
    return props.showAnimationOverride
  }
  return textWidth.value > containerWidth.value
})

const animationClasses = computed(() => ({
  ['f-marquee-text--animation']: showAnimation.value,
  ['f-marquee-text--paused']: props.paused,
}))

const containerStyle = computed(() => {
  return {
    '--gap': `${props.space}px`,
    '--speed': `${props.speed}s`,
    '--direction': props.reverse ? 'reverse' : 'normal',
  }
})
</script>

<style lang="scss" scoped>
.f-marquee-text {
  overflow: hidden;
  min-width: 0;
}

.f-marquee-text__content {
  display: flex;
  white-space: nowrap;

  .f-marquee-text--animation & {
    gap: var(--gap);
    padding-right: var(--gap);
    width: max-content;
    animation: marquee-move-text var(--speed, 20s) linear infinite var(--direction, forwards);
  }

  .f-marquee-text--paused & {
    animation-play-state: paused;
  }
}

@keyframes marquee-move-text {
  to {
    transform: translateX(-50%);
  }
}
</style>
